import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, ChevronRight, X, Play, Pause, Home,
  CheckCircle2, Circle, BookOpen, Video, ArrowRight,
  Maximize2, Minimize2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { ScrollArea } from '../ui/scroll-area';

// Comprehensive tutorial content with detailed explanations
const TUTORIAL_CONTENT = {
  'welcome': {
    title: 'Welcome to KEY-MADE',
    subtitle: 'Your Complete Project Management Solution',
    slides: [
      {
        title: 'What is KEY-MADE?',
        content: `KEY-MADE stands for **Keystone - Managing Architectural Design Execution**. 

It's a comprehensive project management platform designed specifically for **Architectural and Interior Designers** to manage their entire workflow from client consultation to project completion.

**KEY-MADE helps you:**
• Organize projects with clients and contractors
• Collect and manage product clips from anywhere on the web
• Create detailed estimates and invoices
• Collaborate with clients through the Drawing Room portal
• Manage contractors through the Site Office portal
• Generate professional documents and contracts`,
        highlight: 'dashboard-overview',
      },
      {
        title: 'Your Dashboard (My Desk)',
        content: `The **My Desk** dashboard is your command center. Here you'll find:

**Left Sidebar Navigation:**
• Quick access to all KEY-MADE sections
• Collapsible for more screen space
• Color-coded icons for easy recognition

**Main Content Area:**
• Your active projects displayed as cards
• Calendar showing upcoming tasks and meetings
• Weather widget for site visit planning
• Quick actions for common tasks

**Project Cards:**
• Show project name, client, and status
• Display current phase in the journey
• Click to enter the Project Hub`,
        highlight: 'dashboard-layout',
      },
      {
        title: 'Navigation Overview',
        content: `**Main Navigation Areas:**

📁 **My Desk** - Your dashboard with all projects
✂️ **Clips** - Your product library collected from the web
👥 **Contacts** - Client and vendor contact management
👷 **Contractors** - Manage your contractor network
📄 **Documents** - Templates and file storage
💰 **Financials** - Estimates, invoices, and budgets
📚 **Blueprints** - Help guides for every feature
⚙️ **Settings** - Company and account settings

Each section has its own dedicated page with full functionality.`,
        highlight: 'navigation',
      },
    ],
  },
  'project-setup': {
    title: 'Creating & Managing Projects',
    subtitle: 'Set up projects for success',
    slides: [
      {
        title: 'Creating a New Project',
        content: `**To create a new project:**

1. Click the **"New Project"** button on your dashboard
2. Fill in the project details:
   • **Project Name** - A clear, identifiable name
   • **Client** - Select from contacts or create new
   • **Project Type** - Residential, Commercial, etc.
   • **Address** - Project location
   • **Start Date** - Expected project start

3. Click **"Create Project"** to save

**Pro Tip:** Use consistent naming conventions like "LastName - ProjectType" for easy searching (e.g., "Smith - Kitchen Remodel").`,
        highlight: 'create-project',
      },
      {
        title: 'The Project Hub',
        content: `Each project has its own **Project Hub** - a dedicated workspace with everything you need.

**Project Hub Tabs:**
• **Overview** - Project summary and key metrics
• **Floor Plans** - Upload and annotate plans
• **Mood Boards** - Create design presentations
• **Clips** - Products assigned to this project
• **Estimates** - Financial proposals for the project
• **Portals** - Client and contractor access links
• **Documents** - Project-specific files

**The Journey Section:**
Track progress through customizable phases from Initial Consultation to Project Complete.`,
        highlight: 'project-hub',
      },
      {
        title: 'Project Phases & Journey',
        content: `**KEY-MADE tracks projects through phases:**

1. **PreDesign (PD)** - Initial consultation and planning
2. **Schematic Design (SD)** - Concept development
3. **Design Development (DD)** - Detailed design work
4. **Construction Documents (CD)** - Technical drawings
5. **Bidding (BID)** - Contractor selection
6. **Construction Admin (CA)** - Build oversight
7. **Project Closeout (PC)** - Final completion

**Customize Phases:**
Go to Settings → Phase Templates to create your own workflow phases that match how your firm operates.`,
        highlight: 'project-phases',
      },
    ],
  },
  'clipper-tool': {
    title: 'The Clip Library',
    subtitle: 'Organize products from anywhere',
    slides: [
      {
        title: 'What is the Clip Library?',
        content: `The **Clip Library** is your central repository for all products and materials you discover while designing.

**Think of it as a digital scrapbook** where you save:
• Furniture pieces
• Lighting fixtures
• Fabrics and textiles
• Hardware and finishes
• Appliances
• Art and accessories

**Each clip stores:**
• Product image
• Price and vendor
• Specifications
• Your notes
• Links to purchase`,
        highlight: 'clips-overview',
      },
      {
        title: 'Organizing Your Clips',
        content: `**Filter and organize clips by:**

• **Project** - See clips for a specific project
• **Room** - Filter by room type (Kitchen, Bedroom, etc.)
• **Category** - Furniture, Lighting, Textiles, etc.
• **Vendor** - Filter by supplier
• **Approval Status** - Pending, Approved, Rejected

**Bulk Actions:**
Select multiple clips to:
• Assign to a project
• Move to a different room
• Delete in bulk
• Export to a list`,
        highlight: 'clips-filtering',
      },
      {
        title: 'Using Clips in Projects',
        content: `**Clips flow through your workflow:**

1. **Save** - Clip products while browsing vendor sites
2. **Assign** - Add clips to specific projects and rooms
3. **Present** - Share via Drawing Room for client approval
4. **Approve** - Client approves, clip gets a SpecNum
5. **Procure** - Approved clips go to estimates and POs

**SpecNum System:**
Once approved, each clip receives a unique SpecNum (like SK001, SK002) for tracking through procurement.

**Drawing Room Integration:**
Clips can be arranged on mood boards and shared with clients for visual approval.`,
        highlight: 'clips-workflow',
      },
    ],
  },
  'browser-extension': {
    title: 'Chrome Web Clipper Extension',
    subtitle: 'Save products with one click',
    slides: [
      {
        title: 'Installing the Extension',
        content: `**The KEY-MADE Web Clipper** is a Chrome browser extension that lets you save products while browsing any website.

**Installation:**
1. The extension files are in /chrome-extension-v2/
2. Open Chrome → Extensions → Developer Mode
3. Click "Load Unpacked" and select the folder
4. The KEY-MADE icon appears in your toolbar

**Note:** The extension requires you to be logged into KEY-MADE to save clips.`,
        highlight: 'extension-install',
      },
      {
        title: 'Clipping Products',
        content: `**When you find a product you like:**

1. Click the floating **KEY-MADE button** on the page
2. The clipper panel slides out
3. **Automatically captured:**
   • Product image
   • Page title/name
   • Current price
   • Page URL

4. **You can add:**
   • Select a project to assign
   • Choose a room
   • Add notes
   • Adjust specifications

5. Click **"Save Clip"** to add to your library`,
        highlight: 'extension-usage',
      },
      {
        title: 'Best Practices',
        content: `**Tips for effective clipping:**

✅ **Clip from product detail pages** - Best data capture
✅ **Add notes immediately** - Remember why you liked it
✅ **Assign to projects early** - Keeps library organized
✅ **Include vendor info** - Helps with procurement later

❌ **Avoid clipping from search results** - Less detail captured
❌ **Don't forget pricing** - Verify it's current

**Supported Sites:**
The clipper works on most e-commerce and vendor websites. For best results, clip from the individual product page.`,
        highlight: 'extension-tips',
      },
    ],
  },
  'estimates': {
    title: 'Creating Estimates',
    subtitle: 'Professional financial proposals',
    slides: [
      {
        title: 'Estimate Overview',
        content: `**Estimates in KEY-MADE** are detailed financial proposals you create for clients.

**An estimate includes:**
• Line items with products/services
• Quantities and unit prices
• Design fees and markups
• Taxes and totals
• Terms and conditions

**Estimates can be:**
• Created from scratch
• Auto-filled from approved clips
• Generated from templates
• Converted to invoices`,
        highlight: 'estimates-overview',
      },
      {
        title: 'Building an Estimate',
        content: `**Creating an estimate:**

1. Go to **Financials → Estimates**
2. Click **"New Estimate"**
3. Select the **project**

**Adding Line Items:**
• **Manual entry** - Type product name, qty, price
• **From Clips** - Import approved clips
• **Auto-Fill** - Pull from TakeOff rooms automatically

**Organize by Room:**
Group line items by room for clear client presentation.

**Apply Markups:**
Set your design fee percentage that applies to all items.`,
        highlight: 'estimates-creation',
      },
      {
        title: 'Estimate to Invoice',
        content: `**After client approval:**

1. **Generate Documents** - Create contracts, proposals, or material lists from the estimate
2. **Create Invoice** - Convert approved estimate to an invoice
3. **Track Payments** - Monitor payment status

**Document Generation:**
Click "Generate Document" on any estimate to create:
• Formal Contracts
• Proposal Letters
• Material Lists
• Purchase Lists

Each uses customizable templates.`,
        highlight: 'estimates-documents',
      },
    ],
  },
  'invoicing': {
    title: 'Invoice Management',
    subtitle: 'Get paid professionally',
    slides: [
      {
        title: 'Creating Invoices',
        content: `**Invoices in KEY-MADE** help you bill clients professionally.

**Invoice creation methods:**
• **From Estimate** - Convert approved estimate
• **From Scratch** - Manual entry
• **Recurring** - Set up repeat invoices

**Invoice includes:**
• Your company branding
• Client information
• Itemized charges
• Payment terms
• Due date`,
        highlight: 'invoices-overview',
      },
      {
        title: 'Invoice Status Tracking',
        content: `**Track invoices through their lifecycle:**

📝 **Draft** - Still editing, not sent
📤 **Sent** - Delivered to client
👀 **Viewed** - Client has opened it
💰 **Paid** - Payment received
⚠️ **Overdue** - Past due date

**Automated Reminders:**
Set up reminder emails for overdue invoices.

**Payment Recording:**
Mark invoices as paid and record payment method/date.`,
        highlight: 'invoices-status',
      },
      {
        title: 'Accounting Integration',
        content: `**Connect to accounting software:**

KEY-MADE can sync with:
• **Xero** - Full integration available
• **QuickBooks** - Coming soon

**What syncs:**
• Invoice data
• Client information
• Payment records

**Setup:**
Go to Settings → Integrations to connect your accounting software.

*Note: Integration requires setup with your accounting provider credentials.*`,
        highlight: 'invoices-accounting',
      },
    ],
  },
  'client-portals': {
    title: 'Client Portals (Drawing Room)',
    subtitle: 'Collaborate with clients',
    slides: [
      {
        title: 'What is the Drawing Room?',
        content: `The **Drawing Room** is a client-facing portal where you share designs and get approvals.

**Clients can:**
• View mood boards you've created
• See product selections organized by room
• Approve or reject individual items
• Leave comments and feedback
• Access project documents

**You control:**
• What content is visible
• When to share updates
• Approval workflows`,
        highlight: 'drawing-room-overview',
      },
      {
        title: 'Sharing with Clients',
        content: `**To share the Drawing Room:**

1. Open the **Project Hub**
2. Go to the **Portals** tab
3. Click **"Generate Drawing Room Link"**
4. Send the secure link to your client

**The link provides:**
• Password-protected access
• View-only by default
• Ability to approve items
• Comment functionality

**Pro Tip:** Schedule a video call to walk clients through the Drawing Room for the first time.`,
        highlight: 'drawing-room-sharing',
      },
      {
        title: 'Approval Workflow',
        content: `**How approvals work:**

1. You add clips to mood boards
2. Share Drawing Room with client
3. Client reviews each item
4. Client clicks **Approve** or **Request Change**
5. Approved items get a **SpecNum**
6. SpecNum items flow to procurement

**After Approval:**
Approved items automatically:
• Receive sequential SpecNum (SK001, SK002...)
• Move to procurement queue
• Can be added to estimates
• Ready for Purchase Orders`,
        highlight: 'drawing-room-approvals',
      },
    ],
  },
  'documents': {
    title: 'Document Library',
    subtitle: 'Templates and file management',
    slides: [
      {
        title: 'Document Organization',
        content: `The **Document Library** stores all your files and templates.

**Document Types:**
• **Templates** - Reusable document formats
• **Contracts** - Client agreements
• **Proposals** - Project proposals
• **Specifications** - Technical docs
• **Project Files** - Uploaded documents

**Organization:**
Documents can be:
• Global (available to all projects)
• Project-specific
• Organized by category`,
        highlight: 'documents-overview',
      },
      {
        title: 'Using Templates',
        content: `**Templates save time on repeated documents:**

**Available template types:**
• Contract templates
• Proposal templates
• Material list templates
• Scope statement templates

**Using a template:**
1. Go to Documents → Templates
2. Select template to customize
3. Variables auto-fill from project data
4. Edit as needed
5. Export as PDF

**Create Your Own:**
Build templates with merge fields like {client_name}, {project_address}, etc.`,
        highlight: 'documents-templates',
      },
      {
        title: 'Document Generation',
        content: `**Generate documents from estimates:**

From any estimate, click **"Generate Document"** to create:

📄 **Contract** - Formal client agreement
📋 **Proposal** - Detailed project proposal
📦 **Materials List** - All products with specs
🛒 **Purchase List** - Vendor-organized buying list

**The process:**
1. Select document type
2. Choose template
3. Review generated content
4. Edit if needed
5. Export or send

Documents automatically include project details, line items, and totals.`,
        highlight: 'documents-generation',
      },
    ],
  },
};

// Tutorial Slideshow Component
const TutorialSlideshow = ({ chapterId, onClose, onComplete, startingRoute }) => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const chapter = TUTORIAL_CONTENT[chapterId];
  
  if (!chapter) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center">
        <div className="bg-white rounded-xl p-8 max-w-md text-center">
          <p className="text-gray-600">Tutorial content not available for this chapter.</p>
          <Button onClick={onClose} className="mt-4">Close</Button>
        </div>
      </div>
    );
  }
  
  const slides = chapter.slides;
  const totalSlides = slides.length;
  const currentSlideData = slides[currentSlide];
  const progress = ((currentSlide + 1) / totalSlides) * 100;
  
  const goNext = () => {
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide(prev => prev + 1);
    } else {
      // Tutorial complete
      onComplete?.();
      // Return to starting route if it was Blueprint Library
      if (startingRoute === '/blueprints') {
        navigate('/blueprints');
      }
      onClose();
    }
  };
  
  const goPrev = () => {
    if (currentSlide > 0) {
      setCurrentSlide(prev => prev - 1);
    }
  };
  
  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Parse markdown-like content to JSX
  const renderContent = (text) => {
    const lines = text.split('\n');
    return lines.map((line, idx) => {
      // Bold text
      line = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      
      // Check for bullet points
      if (line.trim().startsWith('•') || line.trim().startsWith('✅') || line.trim().startsWith('❌')) {
        return <p key={idx} className="ml-4 my-1" dangerouslySetInnerHTML={{ __html: line }} />;
      }
      
      // Check for numbered lists
      if (/^\d+\./.test(line.trim())) {
        return <p key={idx} className="ml-4 my-1" dangerouslySetInnerHTML={{ __html: line }} />;
      }
      
      // Empty lines become spacing
      if (line.trim() === '') {
        return <div key={idx} className="h-3" />;
      }
      
      return <p key={idx} className="my-1" dangerouslySetInnerHTML={{ __html: line }} />;
    });
  };

  return (
    <div className={`fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4 ${isFullscreen ? 'p-0' : ''}`}>
      <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-2xl flex flex-col overflow-hidden transition-all duration-300 ${
        isFullscreen ? 'w-full h-full rounded-none' : 'max-w-4xl w-full max-h-[90vh]'
      }`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-[#70170F] to-[#8B1E14] px-6 py-4 text-white flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BookOpen className="w-6 h-6" />
              <div>
                <h2 className="font-bold text-lg">{chapter.title}</h2>
                <p className="text-white/80 text-sm">{chapter.subtitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-white/80">
                {currentSlide + 1} / {totalSlides}
              </span>
              <button
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              >
                {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title="Close tutorial"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="mt-3">
            <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-white rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
        
        {/* Slide dots */}
        <div className="flex items-center justify-center gap-2 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          {slides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => goToSlide(idx)}
              className={`w-3 h-3 rounded-full transition-all ${
                idx === currentSlide 
                  ? 'bg-[#70170F] scale-125' 
                  : idx < currentSlide 
                    ? 'bg-green-500' 
                    : 'bg-gray-300 dark:bg-gray-600'
              }`}
              title={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
        
        {/* Content */}
        <ScrollArea className="flex-1 overflow-auto">
          <div className="p-8">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-[#70170F] text-white flex items-center justify-center text-lg font-bold">
                {currentSlide + 1}
              </span>
              {currentSlideData.title}
            </h3>
            
            <div className="prose prose-lg dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 leading-relaxed">
              {renderContent(currentSlideData.content)}
            </div>
          </div>
        </ScrollArea>
        
        {/* Footer Navigation */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
          <Button
            variant="outline"
            onClick={goPrev}
            disabled={currentSlide === 0}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </Button>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                if (startingRoute === '/blueprints') {
                  navigate('/blueprints');
                }
                onClose();
              }}
              className="text-gray-500"
            >
              <Home className="w-4 h-4 mr-2" />
              Back to Library
            </Button>
          </div>
          
          <Button
            onClick={goNext}
            className="bg-[#70170F] hover:bg-[#8B1E14] text-white flex items-center gap-2"
          >
            {currentSlide === totalSlides - 1 ? (
              <>
                <CheckCircle2 className="w-4 h-4" />
                Complete
              </>
            ) : (
              <>
                Next
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export { TutorialSlideshow, TUTORIAL_CONTENT };
export default TutorialSlideshow;
